import React, { useEffect, useMemo, useRef, useState } from 'react';

import i18n from '@i18n';
import PopupNg from '@/ui/PopupNg';
import nikeInto from '../../video/nike-intro.mp4';

import { Button } from 'nike-ui/src/components/button/Button';
import moment from 'moment';
import { streamErrorCodes } from 'nike-ui/src/components/streams/errors';
import {
    getLiveStreamEventInfo,
    ProviderAuthResponse,
    startFupSub,
    stopFupSub
} from 'nike-ui/src/components/streams/services';
import { pushClient } from '@/services/push';
import { dispatch } from 'nike-re-framejs';
import { NikePlayerSource } from 'nike-ui/src/components/streams/players/videojs/types';
import { NikePlayer } from 'nike-ui/src/components/streams/players/NikePlayer';
import { trackVideoStart } from 'nike-ui/src/analytics/events/video';
import { trackDwhVideoEvent } from 'nike-ui/src/components/dwh-tracking/api';
import { getSportNameById } from 'nike-ui/src/common/utils/utils';

export function PlayButtonOverlay({
    sportId,
    onPlayClick
}: {
    sportId: string | number;
    onPlayClick: React.MouseEventHandler;
}) {
    return (
        <div className={`default-sport virtual-sport-id${sportId}`}>
            <div onClick={onPlayClick} className={`default-placeholder streamPlaceholder${sportId}`}>
                <div className="play-button">
                    <div className="arrow-play" />
                    {i18n('react.virtualgames.startStream')}
                </div>
            </div>
        </div>
    );
}

export function ErrorMessageOverlay({ sportId, message, onRestartClick, 'data-atid': dataAtid, isLoggedError, label }) {
    const handleOnClick = () => {
        if (isLoggedError) {
            dispatch(['login.openScreen']);
        } else {
            onRestartClick();
        }
    };

    return (
        <div className={`default-sport virtual-sport-id${sportId}`}>
            <div
                onClick={handleOnClick}
                className={`default-placeholder streamPlaceholder${sportId}`}
                data-atid={dataAtid}
            >
                <div className="error-message">
                    <div className="pb-15">{message}</div>
                    {/*@ts-ignore*/}
                    <Button label={label} primary />
                </div>
            </div>
        </div>
    );
}

const playIntro = refIntro =>
    new Promise((resolve, reject) => {
        const introVideo = refIntro.current;
        const endedHandler = () => {
            resolve(null);
            introVideo.removeEventListener('ended', endedHandler);
        };
        introVideo.addEventListener('ended', endedHandler);
        introVideo.play().catch(reject);
    });

const stopVideo = refIntro => {
    const introVideo = refIntro.current;
    if (introVideo) {
        introVideo.pause();
        introVideo.load();
    }
};

let handleError;

export const ExpirationIntroMessage = ({ expirationIntroDate }) => {
    const expirationIntroStartStorage = Number(localStorage.getItem('expirationIntroStart'));
    const currentDate = new Date().getTime();
    const [expirationIntroStartDisplay, setExpirationIntroStartDisplay] = useState(false);

    const onClick = () => {
        localStorage.setItem('expirationIntroStart', '' + Date.now());
        setExpirationIntroStartDisplay(false);
    };

    useEffect(() => {
        setExpirationIntroStartDisplay(currentDate - expirationIntroStartStorage > 24 * 60 * 60 * 1000);
    }, [currentDate, expirationIntroStartStorage]);

    return expirationIntroDate && expirationIntroStartDisplay ? (
        <div className={`default-placeholder overlay-message py-20 p-abs`}>
            <div className="error-message">
                <div className="pb-15">
                    {i18n('liveStavky.stream.msg.introPeriod', moment(expirationIntroDate).format('DD.MM.YYYY HH:MM'))}
                </div>
                <Button label={'OK'} primary onClick={onClick} />
            </div>
        </div>
    ) : null;
};

const VideoStreamLivePlayer = ({
    eventId,
    sportId,
    tournamentId,
    active
}: {
    eventId: string | number;
    sportId: number;
    tournamentId: string;
    active: boolean;
}) => {
    const [streamInfo, setStreamInfo] = useState<ProviderAuthResponse>(null);
    const [started, setStarted] = useState(false);
    const [shownStream, showStream] = useState(false);
    const introVideoRef = useRef();

    const source: NikePlayerSource = useMemo(
        () => ({
            src: streamInfo?.authUrl,
            type: 'application/x-mpegurl',
            handleManifestRedirects: true,
            providerName: streamInfo?.providerName
        }),
        [streamInfo]
    );

    // Subscripcia kt. sleduje ci sa nespusti stream na inom zariadeni (aby bolo mozne sledovat len na jednom)
    useEffect(() => {
        startFupSub(pushClient, handleError);
        return () => stopFupSub();
    }, []);

    useEffect(() => {
        if (active) {
            setStarted(true);
            Promise.all([
                playIntro(introVideoRef).then(() => showStream(true)),
                getLiveStreamEventInfo(eventId).then(data => setStreamInfo(data))
            ]).catch(e => handleError(e));
        }
    }, [active, eventId]);

    const stop = () => {
        try {
            setStarted(false);
            showStream(false);
            setStreamInfo(null);
            stopVideo(introVideoRef);
        } catch (e) {
            handleError(e);
        }
    };

    handleError = e => {
        console.error('Error while playing video!', e);
        if (streamInfo) {
            stop();
        }
        const errorCode = e?.errorCode;
        const errorParams = e?.errorParams;

        const errorKey = streamErrorCodes[errorCode] || streamErrorCodes.TECHNICAL_PROBLEM;
        const params = errorParams || [];
        const isLoggedError = errorCode === 'USER_NOT_LOGGED_IN';

        if (started) {
            if (isLoggedError) {
                PopupNg.dispatcher.open('dialog/message', {
                    bodyText: i18n(errorKey, ...params),
                    confirmText: i18n('jsp.login.Prihlasit'),
                    onConfirm: () => dispatch(['login.openScreen']),
                    cancelText: i18n('jsp.message.zavriet')
                });
            } else {
                PopupNg.dispatcher.open('dialog/message', {
                    bodyText: i18n(errorKey, ...params)
                });
            }
        }
    };

    const trackVideoData = (eventName: string, videoTime: number) => {
        if (eventName === 'video_start') {
            trackVideoStart({
                provider: source.providerName,
                sport: getSportNameById(sportId, false),
                sportEventId: String(eventId),
                sportId: sportId,
                videoId: source?.src,
                articleId: 'LIVEPLAYER'
            });
        }
        trackDwhVideoEvent(
            eventName,
            source?.src,
            source?.providerName,
            videoTime,
            eventId?.toString(),
            sportId?.toString(),
            tournamentId,
            null
        );
    };

    return (
        <div className={`virtual-stream virtual-stream-wrap ${started ? 'playing' : ''}`}>
            <div className={'p-rel'}>
                <ExpirationIntroMessage expirationIntroDate={streamInfo?.expirationIntroDate} />
            </div>
            <video
                ref={introVideoRef}
                src={nikeInto}
                style={{ display: started && !shownStream ? 'block' : 'none' }}
                className="virtual-stream"
                playsInline
            />

            {streamInfo && (
                <div
                    style={{ display: shownStream ? 'block' : 'none' }}
                    className="virtual-stream special-video-height"
                    data-atid={'live-video-player'}
                    data-provider={source?.providerName}
                >
                    <NikePlayer source={source} autoplay trackVideoData={trackVideoData} />
                </div>
            )}
        </div>
    );
};

export default VideoStreamLivePlayer;
